import axios from '../utils/axiosConfig';
import { AxiosError } from 'axios';

import logger from '../utils/logger';

// Typen für die API-Kommunikation
export interface Exercise {
  name: string;
  moves: string[];
  totalDifficulty: number;
}

export interface ValidationStatus {
  isComplete: boolean;
  isFirstExerciseValid: boolean;
  isFullyValid: boolean;
  isFirstExerciseSelected: boolean;
}

export interface CompetitionCard {
  _id?: string;
  competition: string;
  club: string;
  athlete: string;
  registration: string;
  exercises: Exercise[];
  validationStatus: ValidationStatus;
  selectedRoutines: string[];
  checkboxes: boolean[];
  additionalMarkings: boolean[];
  lastUpdated?: string;
  createdAt?: string;
}

// Service-Funktionen
const getCompetitionCard = async (competitionId: string, clubId: string, athleteId: string, cardIndex?: number): Promise<CompetitionCard | null> => {
  try {
    const params = cardIndex !== undefined ? { params: { cardIndex } } : {};
    const response = await axios.get(`/api/competition-cards/${competitionId}/${clubId}/athlete/${athleteId}`, params);
    return response.data;
  } catch (error) {
    // 404 bedeutet, dass noch keine Karte existiert
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

const getAllCompetitionCards = async (competitionId: string, clubId: string): Promise<CompetitionCard[]> => {
  try {
    const response = await axios.get(`/api/competition-cards/${competitionId}/${clubId}`);
    return response.data;
  } catch (error) {
    //console.error('Fehler beim Abrufen der Wettkampfkarten:', error);
    logger.error('Error fetching competition cards', { error: error });
    throw error;
  }
};

const saveCompetitionCard = async (
  competitionId: string,
  clubId: string,
  athleteId: string,
  cardData: Omit<CompetitionCard, '_id' | 'competition' | 'club' | 'athlete' | 'registration' | 'lastUpdated' | 'createdAt'>,
  options?: { cardIndex?: number; extraCardCompetitionGroup?: string }
): Promise<CompetitionCard> => {
  try {
    const response = await axios.post(
      `/api/competition-cards/${competitionId}/${clubId}/athlete/${athleteId}`,
      {
        ...cardData,
        ...(options?.cardIndex !== undefined ? { cardIndex: options.cardIndex } : {}),
        ...(options?.extraCardCompetitionGroup ? { extraCardCompetitionGroup: options.extraCardCompetitionGroup } : {})
      }
    );
    return response.data;
  } catch (error) {
    //console.error('Fehler beim Speichern der Wettkampfkarte:', error);
    logger.error('Error saving competition card', { error: error });
    throw error;
  }
};

const deleteCompetitionCard = async (
  competitionId: string,
  clubId: string,
  athleteId: string
): Promise<void> => {
  try {
    await axios.delete(`/api/competition-cards/${competitionId}/${clubId}/athlete/${athleteId}`);
  } catch (error) {
    //console.error('Fehler beim Löschen der Wettkampfkarte:', error);
    logger.error('Error deleting competition card', { error: error });
    throw error;
  }
};

export const competitionCardsService = {
  getCompetitionCard,
  getAllCompetitionCards,
  saveCompetitionCard,
  deleteCompetitionCard
};

export default competitionCardsService;